import { Box, useTheme } from '@mui/material';
import { IconChevronDown, IconChevronLeft, IconChevronRight, IconLink } from '@tabler/icons-react';
import { shortMonthLabel, monthLabel } from '../../utils/costOfLiving';

// Fixed slots (#2207): 72 | 266 | 18 | 72 px with 4 px gaps, so nothing in
// the row moves or resizes when the month or the link state changes.
const ARROW_SLOT = 72;
const BOX_WIDTH = 266;
const LINK_SLOT = 18;
const BOX_HEIGHT = 32;
// A closed month's tab is this much taller, reaching into the box below it.
const TAB_EXTRA = 6;

/**
 * Month view switcher (#2139, #2207, epics #2138 and #2201): previous and
 * next arrows carrying the neighbour months' short names, and a dropdown of
 * the months with entries, newest first, each marked "in progress" or
 * "closed". Every part sits in a fixed slot: the previous arrow right-aligned
 * in its slot, the next arrow left-aligned in its own, and the 18 px link
 * slot always reserved, holding the link glyph only while the chart is
 * linked (epic #2138 decision 8, when the arrows also turn link blue).
 *
 * The dropdown is a styled box with the month centered and the chevron
 * pinned 8 px from its right edge; a native `select` covers it at opacity 0,
 * so phones keep the native picker and the label centers the same in every
 * browser. Its focus ring shows on the box (`:focus-within`). Below md the
 * box gives way, up to 266 px wide.
 *
 * `boxed` (a closed month) squares the box's bottom into a tab that opens
 * into the box DashboardN draws around the Bottom line and Money in and out:
 * the tab grows 6 px down and drops its bottom edge, so the row keeps its
 * height.
 *
 * @param {object} props
 * @param {Array<{yearMonth: string, closed: boolean}>} props.months  newest first
 * @param {string} props.value                 the shown month
 * @param {(yearMonth: string) => void} props.onChange
 * @param {boolean} [props.linked]             the chart follows this month
 * @param {boolean} [props.boxed]              the shown month is closed
 * @param {Object} props.strings               engine strings
 * @param {string} props.locale
 */
const MonthSwitcher = ({ months, value, onChange, linked = false, boxed = false, strings, locale }) => {
  const { palette, ds } = useTheme();
  const index = months.findIndex((m) => m.yearMonth === value);
  // Newest first: the previous month is the next entry down the list.
  const previous = index >= 0 ? months[index + 1] : undefined;
  const next = index > 0 ? months[index - 1] : undefined;
  const cue = linked ? palette.cat.checking : palette.interactive;
  const labelOf = (m) => `${monthLabel(m.yearMonth, locale)} (${strings[m.closed ? 'dashboardN.closed' : 'dashboardN.inProgress']})`;
  const shownLabel = index >= 0 ? labelOf(months[index]) : '';
  const edge = boxed ? palette.marker.box : palette.divider;

  const arrowSx = (enabled) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '2px',
    height: '28px',
    mt: '2px',
    border: 0,
    borderRadius: '5px',
    background: 'none',
    padding: '0 4px',
    font: 'inherit',
    fontSize: `${ds.font.meta}px`,
    color: enabled ? cue : palette.text.dim,
    cursor: enabled ? 'pointer' : 'default',
    whiteSpace: 'nowrap',
    '&:focus-visible': { outline: `1.5px solid ${palette.interactive}`, outlineOffset: '2px' },
  });
  const slotSx = (justify) => ({ width: ARROW_SLOT, flex: 'none', display: 'flex', justifyContent: justify, minWidth: 0 });

  return (
    <Box
      data-testid="month-switcher"
      data-linked={linked ? 'true' : 'false'}
      data-boxed={boxed ? 'true' : 'false'}
      sx={{ display: 'flex', alignItems: 'flex-start', gap: '4px', flexWrap: 'nowrap', minWidth: 0, maxWidth: '100%' }}
    >
      <Box data-testid="month-switcher-prev-slot" sx={slotSx('flex-end')}>
        <Box
          component="button"
          type="button"
          aria-label={strings['dashboardN.prevMonth']}
          disabled={!previous}
          onClick={() => previous && onChange(previous.yearMonth)}
          sx={arrowSx(Boolean(previous))}
        >
          <IconChevronLeft size={14} aria-hidden />
          {previous ? shortMonthLabel(previous.yearMonth, locale) : ''}
        </Box>
      </Box>
      <Box
        data-testid="month-switcher-box"
        sx={{
          position: 'relative',
          boxSizing: 'border-box',
          width: { xs: 'auto', md: BOX_WIDTH },
          flex: { xs: `1 1 ${BOX_WIDTH}px`, md: 'none' },
          maxWidth: BOX_WIDTH,
          minWidth: 0,
          height: BOX_HEIGHT + (boxed ? TAB_EXTRA : 0),
          pb: boxed ? `${TAB_EXTRA}px` : 0,
          px: '26px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: `1px solid ${edge}`,
          borderBottomColor: boxed ? palette.surface.raised : edge,
          borderRadius: boxed ? '6px 6px 0 0' : '6px',
          backgroundColor: palette.surface.raised,
          color: palette.text.primary,
          fontSize: '15px',
          '&:hover': { borderColor: boxed ? edge : palette.borderStrong, borderBottomColor: boxed ? palette.surface.raised : palette.borderStrong },
          '&:focus-within': { outline: `1.5px solid ${palette.interactive}`, outlineOffset: '2px' },
        }}
      >
        <Box component="span" data-testid="month-switcher-label" aria-hidden sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {shownLabel}
        </Box>
        <IconChevronDown size={14} aria-hidden style={{ position: 'absolute', right: 8, top: `calc(50% - ${boxed ? 7 + TAB_EXTRA / 2 : 7}px)` }} />
        <Box
          component="select"
          aria-label={strings['dashboardN.monthPicker']}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', font: 'inherit', border: 0, m: 0 }}
        >
          {months.map((m) => (
            <option key={m.yearMonth} value={m.yearMonth}>{labelOf(m)}</option>
          ))}
        </Box>
      </Box>
      <Box
        data-testid="month-switcher-link-slot"
        sx={{ width: LINK_SLOT, height: BOX_HEIGHT, flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        {linked && <IconLink size={14} aria-hidden data-testid="month-switcher-link" style={{ color: cue }} />}
      </Box>
      <Box data-testid="month-switcher-next-slot" sx={slotSx('flex-start')}>
        <Box
          component="button"
          type="button"
          aria-label={strings['dashboardN.nextMonth']}
          disabled={!next}
          onClick={() => next && onChange(next.yearMonth)}
          sx={arrowSx(Boolean(next))}
        >
          {next ? shortMonthLabel(next.yearMonth, locale) : ''}
          <IconChevronRight size={14} aria-hidden />
        </Box>
      </Box>
    </Box>
  );
};

export default MonthSwitcher;
